// Small helpers used across the customer app: restaurant open/closed logic,
// distance formatting, and Grenada-friendly GPS math.

/**
 * Returns true when the restaurant is accepting orders right now.
 * Uses the server-provided opens_at + closes_at (HH:MM, 24-hour, local time).
 * If either bound is missing, we assume the restaurant is open (fail-open).
 * Handles wrap-around (e.g. opens 18:00, closes 02:00).
 */
export function isRestaurantOpen(restaurant, now = new Date()) {
  if (!restaurant) return false;
  const opens = parseHm(restaurant.opens_at);
  const closes = parseHm(restaurant.closes_at);
  if (opens == null || closes == null) return true;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  if (closes > opens) {
    return nowMin >= opens && nowMin < closes;
  }
  // Wrap-around (crosses midnight)
  return nowMin >= opens || nowMin < closes;
}

export function parseHm(hm) {
  if (!hm || typeof hm !== "string") return null;
  const [h, m] = hm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Great-circle distance between two lat/lng points, in miles.
 * Returns null if either point is missing.
 */
export function distanceMiles(from, to) {
  if (!from || !to) return null;
  if (from.lat == null || from.lng == null || to.lat == null || to.lng == null) return null;
  const R = 6371; // km
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos((from.lat * Math.PI) / 180) *
            Math.cos((to.lat * Math.PI) / 180) *
            Math.sin(dLng / 2) ** 2;
  const km = 2 * R * Math.asin(Math.sqrt(a));
  return km / 1.60934;
}

export function formatMiles(miles) {
  if (miles == null) return null;
  if (miles < 0.1) return "0.1 mi";
  return `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
}
